/**
 * ModelSelector — Universal LLM model picker
 * Shows: Direct API models + Via OpenRouter models + Custom models
 * Persists selection to localStorage per lab.
 * onChange(openrouterModelId) is called when user selects.
 * Pass null/undefined to reset to default.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, Cpu, Globe, Plus, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'ideaworks_models_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 min

function getStorageKey(lab) {
  return `ideaworks_model_${lab}`;
}

export default function ModelSelector({ lab, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(null); // { openrouter_id, display } or null = default
  const dropRef = useRef(null);

  // Load persisted selection
  useEffect(() => {
    try {
      const saved = localStorage.getItem(getStorageKey(lab));
      if (saved) setSelected(JSON.parse(saved));
    } catch { }
  }, [lab]);

  // Fetch models (cached)
  useEffect(() => {
    const cached = (() => {
      try {
        const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
        if (c && Date.now() - c.ts < CACHE_TTL) return c.data;
      } catch { }
      return null;
    })();
    if (cached) { setGroups(cached); return; }

    axios.get(`${API}/api/llm/models-for-selector`).then(({ data }) => {
      setGroups(data);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
      } catch { }
    }).catch(() => {
      // Provide a minimal fallback so the UI doesn't break
      setGroups({
        default: { openrouter_id: 'anthropic/claude-sonnet-4-6', display: 'Claude Sonnet 4.6', source: 'direct' },
        direct: [],
        openrouter: [],
        custom: [],
      });
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (model) => {
    const val = model ? { openrouter_id: model.openrouter_id, display: model.display } : null;
    setSelected(val);
    try {
      if (val) localStorage.setItem(getStorageKey(lab), JSON.stringify(val));
      else localStorage.removeItem(getStorageKey(lab));
    } catch { }
    onChange?.(val?.openrouter_id || null);
    setOpen(false);
  };

  const currentLabel = selected?.display || groups?.default?.display || 'Default Model';
  const isOverride = !!selected;

  const SourceIcon = ({ source }) =>
    source === 'direct' ? <Cpu size={9} style={{ color: '#a68eff' }} /> :
    source === 'custom' ? <Plus size={9} style={{ color: '#f0c27f' }} /> :
    <Globe size={9} style={{ color: '#00d4b8' }} />;

  const ModelRow = ({ model, isActive }) => (
    <button
      type="button"
      onClick={() => select(model)}
      className="w-full flex items-center gap-2 px-3 py-2 text-left transition-all hover:opacity-90"
      style={{
        background: isActive ? 'rgba(124,94,250,0.12)' : 'transparent',
        borderBottom: '1px solid rgba(255,255,255,0.03)',
      }}
    >
      <SourceIcon source={model.source} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold truncate" style={{ color: isActive ? '#a68eff' : 'rgba(255,255,255,0.8)' }}>
          {model.display}
        </div>
        {model.description && (
          <div className="text-[9px] truncate" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {model.description}
          </div>
        )}
      </div>
      {model.cost_input_per_1m !== undefined && (
        <span className="text-[9px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.25)' }}>
          ${model.cost_input_per_1m}/M
        </span>
      )}
      {isActive && <Check size={10} style={{ color: '#a68eff', flexShrink: 0 }} />}
    </button>
  );

  const SectionLabel = ({ label, icon: Icon, color }) => (
    <div className="flex items-center gap-1.5 px-3 py-1.5 sticky top-0"
      style={{ background: 'rgba(10,10,20,0.97)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <Icon size={9} style={{ color }} />
      <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color }}>{label}</span>
    </div>
  );

  return (
    <div className={`relative inline-block ${className}`} ref={dropRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`model-selector-${lab}`}
        title="Select AI model"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all hover:opacity-80"
        style={{
          background: isOverride ? 'rgba(124,94,250,0.15)' : 'rgba(255,255,255,0.06)',
          border: `1px solid ${isOverride ? 'rgba(124,94,250,0.4)' : 'rgba(255,255,255,0.1)'}`,
        }}
      >
        <Cpu size={11} style={{ color: isOverride ? '#a68eff' : 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
        <span className="text-[11px] font-semibold max-w-[140px] truncate"
          style={{ color: isOverride ? '#a68eff' : 'rgba(255,255,255,0.5)' }}>
          {currentLabel}
        </span>
        {isOverride && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); select(null); }}
            className="ml-0.5 hover:opacity-80"
            style={{ color: 'rgba(124,94,250,0.6)' }}
          >
            <X size={10} />
          </button>
        )}
        <ChevronDown size={10} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 mt-1 rounded-xl overflow-hidden"
          style={{
            top: '100%',
            right: 0,
            width: 260,
            background: 'rgba(10,10,20,0.98)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
            maxHeight: 340,
            overflowY: 'auto',
          }}
        >
          {/* Default option */}
          <div>
            <SectionLabel label="Default" icon={Cpu} color="#7c5efa" />
            <button
              type="button"
              onClick={() => select(null)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left transition-all hover:opacity-90"
              style={{
                background: !selected ? 'rgba(124,94,250,0.12)' : 'transparent',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <Cpu size={9} style={{ color: '#a68eff' }} />
              <span className="flex-1 text-[11px] font-semibold" style={{ color: !selected ? '#a68eff' : 'rgba(255,255,255,0.7)' }}>
                {groups?.default?.display || 'Claude Sonnet 4.6 (Default)'}
              </span>
              {!selected && <Check size={10} style={{ color: '#a68eff' }} />}
            </button>
          </div>

          {/* Direct API models */}
          {groups?.direct?.length > 0 && (
            <div>
              <SectionLabel label="Direct API" icon={Cpu} color="#a68eff" />
              {groups.direct.map(m => (
                <ModelRow
                  key={m.openrouter_id}
                  model={m}
                  isActive={selected?.openrouter_id === m.openrouter_id}
                />
              ))}
            </div>
          )}

          {/* Via OpenRouter models */}
          {groups?.openrouter?.length > 0 && (
            <div>
              <SectionLabel label="Via OpenRouter" icon={Globe} color="#00d4b8" />
              {groups.openrouter.map(m => (
                <ModelRow
                  key={m.openrouter_id}
                  model={m}
                  isActive={selected?.openrouter_id === m.openrouter_id}
                />
              ))}
            </div>
          )}

          {/* Custom models */}
          {groups?.custom?.length > 0 && (
            <div>
              <SectionLabel label="Custom" icon={Plus} color="#f0c27f" />
              {groups.custom.map(m => (
                <ModelRow
                  key={m.openrouter_id}
                  model={m}
                  isActive={selected?.openrouter_id === m.openrouter_id}
                />
              ))}
            </div>
          )}

          {/* Footer hint */}
          <div className="px-3 py-2 text-[9px] text-center"
            style={{ color: 'rgba(255,255,255,0.2)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            Add custom models in Settings → Custom LLMs
          </div>
        </div>
      )}
    </div>
  );
}
